import { FC, ReactNode } from 'react'
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ModalProps,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from '@dhl/icons'

import { SideMenuInline } from './SideMenuInline.js'
import { SideMenuNodes } from './SideMenuNodes.js'
import { MenuItemWithSubItems } from './types.js'
import { SideMenuWithCollapsedSubmenu } from './SideMenuWithCollapsedSubmenu.js'

type Props = {
  collapsedSubmenu?: boolean
  isDrawerOpen?: boolean
  onClose?: ModalProps['onClose']
  disableTab: boolean
  drawerWidth: number
  hasInformationBar?: boolean
  inlineSubMenuItems: boolean
  isExpanded?: boolean
  menuItems: MenuItemWithSubItems[]
  bottomMenuItems?: MenuItemWithSubItems[]
  navigationPath?: string
  StartComponent?: ReactNode
  EndComponent?: ReactNode
  toggleExpanded?: () => void
}

export const SideMenu: FC<Props> = ({
  collapsedSubmenu = false,
  isDrawerOpen,
  onClose,
  disableTab,
  drawerWidth,
  hasInformationBar = false,
  inlineSubMenuItems,
  isExpanded = false,
  menuItems,
  bottomMenuItems,
  navigationPath,
  StartComponent,
  EndComponent,
  toggleExpanded,
}) => {
  const { t } = useTranslation()
  const tabIndexProp = disableTab ? { tabIndex: -1 } : {}
  const theme = useTheme()

  const isEmptyMenu = menuItems.length === 0

  if (isEmptyMenu) {
    return null
  }

  const setAnimation = () => ({
    width: isExpanded ? drawerWidth : `calc(${theme.spacing(7)} + 1px)`,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: isExpanded ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
  })

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={onClose}
      variant={isDrawerOpen === undefined ? 'permanent' : undefined}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        flexShrink: 0,
        whiteSpace: 'nowrap',
        overflowX: !isExpanded ? 'visible' : 'hidden',
        displayPrint: 'none',
        ...setAnimation(),
        '.MuiPaper-root': {
          border: 0,
          overflowX: isExpanded ? 'visible' : 'hidden',
          borderRight: ({ palette }) => `1px solid ${palette.grey[300]}`,
          paddingTop: ({ spacing }) => spacing(hasInformationBar ? 10 : 6),
          ...setAnimation(),
        },
      }}
    >
      <List
        component="nav"
        // TODO: phase out data-test, instead use data-testid
        data-test="side-navigation"
        data-testid="side-navigation"
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        {StartComponent && isExpanded && (
          <>
            {StartComponent}
            <Divider />
          </>
        )}
        {inlineSubMenuItems && collapsedSubmenu && (
          <SideMenuWithCollapsedSubmenu
            disableTab={disableTab}
            isExpanded={isExpanded}
            toggleExpanded={toggleExpanded}
            menuItems={menuItems}
            navigationPath={navigationPath}
          />
        )}

        {inlineSubMenuItems && !collapsedSubmenu && (
          <SideMenuInline
            disableTab={disableTab}
            isExpanded={isExpanded}
            menuItems={menuItems}
            navigationPath={navigationPath}
          />
        )}
        {!inlineSubMenuItems && (
          <SideMenuNodes
            disableTab={disableTab}
            isExpanded={isExpanded}
            menuItems={menuItems}
            navigationPath={navigationPath}
          />
        )}
        <Divider />
        {toggleExpanded && (
          <ListItemButton
            aria-label="collapse/expand"
            onClick={toggleExpanded}
            data-testid="side-navigation-toggle"
            {...tabIndexProp}
          >
            <ListItemIcon
              sx={{
                '&> svg': {
                  borderRadius: '50%',
                  backgroundColor: theme.palette.grey[300],
                },
              }}
            >
              {isExpanded ? (
                <ChevronLeft htmlColor={theme.palette.common.black} />
              ) : (
                <ChevronRight htmlColor={theme.palette.common.black} />
              )}
            </ListItemIcon>

            <ListItemText
              primary={<Typography>{t('components.page.sideMenu.collapse')}</Typography>}
              disableTypography
            />
          </ListItemButton>
        )}

        {bottomMenuItems && bottomMenuItems.length > 0 && (
          <Box marginTop="auto">
            {inlineSubMenuItems && (
              <SideMenuInline
                disableTab={disableTab}
                isExpanded={isExpanded}
                menuItems={bottomMenuItems}
                navigationPath={navigationPath}
              />
            )}
            {!inlineSubMenuItems && (
              <SideMenuNodes
                disableTab={disableTab}
                isExpanded={isExpanded}
                menuItems={bottomMenuItems}
                navigationPath={navigationPath}
              />
            )}
          </Box>
        )}
      </List>
      {EndComponent && isExpanded && (
        <Box mt="auto" pb={3}>
          {EndComponent}
        </Box>
      )}
    </Drawer>
  )
}
